import {getCurrentScriptUrlContext} from '../../utils/getCurrentScriptUrlContext';
import type {ServiceEnvironment} from '../../utils/getTargetEnvironment';
import {
  getDevboxHost,
  getTargetEnvironment,
  getTunnelHost,
  getTurboHost,
} from '../../utils/getTargetEnvironment';
import {isDevEnvironment} from '../../utils/isDevEnvironment';
import type {AuthenticatedUserFragmentData} from '../../data-layer-client/queries/authenticatedUserFragment';
import type {AccountSessionClaim} from '../types';
import {assertUnreachable} from '../../utils/assertUnreachable';

export type AuthenticationState =
  | {status: 'unauthenticated'; userSessionExpired: boolean}
  | {
      status: 'user_authenticated';
      expires: Date;
      user: AuthenticatedUserFragmentData | null;
      publishableKey?: string;
      accountSessionClaim?: AccountSessionClaim;
    }
  | {
      status: 'account_session_authenticated';
      expires: Date;
      userSessionExpired: boolean;
    }
  | {
      status: 'error';
      error: Error;
    };

export type AuthenticationStatus = AuthenticationState['status'];

export const currentEnvironment = () =>
  getCurrentScriptUrlContext().serviceEnvironment;

export function getTargetOriginForAuthContext(
  targetEnvironment: ServiceEnvironment = currentEnvironment(),
  isDevelopmentBuild: boolean = isDevEnvironment,
) {
  const env = getTargetEnvironment(targetEnvironment, {isDevelopmentBuild});

  switch (env) {
    case 'prod':
      return 'https://connect.stripe.com';
    case 'preprod':
      return 'https://preprod-connect.stripe.com';
    case 'qa':
      return 'https://qa-connect.stripe.com';
    case 'tunnel':
      return `https://${getTunnelHost('manage')}`; // i.e. https://jorgea-manage.tunnel.stripe.me
    case 'devbox':
      return `https://${getDevboxHost('manage')}`; // i.e. https://jorgea-0-g6v1--manage-mydev.dev.stripe.me
    case 'turbo':
      return `https://${getTurboHost('connect')}`;
    default:
      assertUnreachable(env);
      throw new Error(`Unknown ServiceEnvironment: ${env}`);
  }
}

export function isAllowedAuthOrigin(origin: string) {
  const expectedOrigin = getTargetOriginForAuthContext();

  return expectedOrigin === origin;
}

export type AuthenticationKeyType =
  | 'account_session'
  | 'stripe_user'
  | 'external_account';

export type KeyAndType = {
  key: string;
  type: AuthenticationKeyType;
};
